import { useState, useEffect } from "react";
import { X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useLocation } from "@/lib/router-compat";
import HomieChat from "./HomieChat";
import { HOMIE_OPEN_EVENT, type HomieOpenDetail } from "@/lib/homieBridge";

const HomieBubble = () => {
  const location = useLocation();
  const isEmbed = location.pathname.startsWith("/embed");
  // Only hide the bubble on the full-page chat itself — /homie is now the quiz landing.
  const isHomie = location.pathname === "/homie/chat";
  const [open, setOpen] = useState(false);
  const [seedPrompt, setSeedPrompt] = useState<string | undefined>(undefined);

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<HomieOpenDetail>).detail || {};
      if (detail.prompt) setSeedPrompt(detail.prompt);
      setOpen(true);
    };
    window.addEventListener(HOMIE_OPEN_EVENT, handler);
    return () => window.removeEventListener(HOMIE_OPEN_EVENT, handler);
  }, []);

  if (isEmbed || isHomie) return null;

  return (
    <>
      {/* Floating button */}
      <AnimatePresence>
        {!open && (
          <motion.button
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            exit={{ scale: 0 }}
            onClick={() => setOpen(true)}
            className="fixed bottom-20 right-4 sm:bottom-6 sm:right-6 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg hover:shadow-xl hover:scale-105 transition-all"
            aria-label="Chat with Homie"
          >
            <span className="text-2xl">🏠</span>
          </motion.button>
        )}
      </AnimatePresence>

      {/* Chat panel */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="fixed bottom-2 right-2 sm:bottom-6 sm:right-6 z-50 flex w-[380px] max-w-[calc(100vw-1rem)] flex-col overflow-hidden rounded-2xl border border-border bg-card shadow-2xl"
            style={{ height: "min(560px, calc(100vh - 3rem))" }}
          >
            {/* Close button overlay */}
            <button
              onClick={() => { setOpen(false); setSeedPrompt(undefined); }}
              className="absolute top-3 right-3 z-10 text-primary-foreground/80 hover:text-primary-foreground p-1"
            >
              <X className="h-5 w-5" />
            </button>

            <HomieChat seedPrompt={seedPrompt} onSeedConsumed={() => setSeedPrompt(undefined)} />
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default HomieBubble;
